/**
 * Contracts & Tariffs: one page with the contract list and the tariff
 * configuration as tabs. The tab lives in the URL (`?tab=tariffs`), so a
 * reload, Back and a shared link land on the same tab.
 */

import { Trans } from "@lingui/react/macro";
import { FileText, Calculator } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { useUrlTab } from "@/hooks/useUrlTab";

import ContractsPage from "./billing/ContractsPage";
import TariffsPage from "./billing/TariffsPage";

const TABS = ["contracts", "tariffs"] as const;
type ContractsTariffsTab = (typeof TABS)[number];

export default function ContractsTariffsPage() {
  const [activeTab, setActiveTab] = useUrlTab<ContractsTariffsTab>("tab", TABS, "contracts");

  return (
    <PageContainer width="list">
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ContractsTariffsTab)}>
        <PageHeader
          title={<Trans id="nav.contractsTariffs">Contracts & Tariffs</Trans>}
          tabs={
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="contracts" className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden="true" />
                <Trans id="contracts.title">Contracts</Trans>
              </TabsTrigger>
              <TabsTrigger value="tariffs" className="flex items-center gap-2">
                <Calculator className="h-4 w-4" aria-hidden="true" />
                <Trans id="tariffs.tab">Tariffs</Trans>
              </TabsTrigger>
            </TabsList>
          }
        />

        <TabsContent value="contracts">
          <ContractsPage />
        </TabsContent>

        <TabsContent value="tariffs">
          <TariffsPage />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
